import { ApiData, ApiDescription, RequestsMethod } from '../clientTypes';
import {
  ContractLightDTO,
  ContractRowDTO,
  PandaDocDocumentDTO,
  SearchContractsDTO,
  SmartBillSyncRunDTO,
  SmartBillSyncStatusDTO,
  UnmappedInvoiceDTO,
} from '../clientTypes/DTOs/ContractsDTO';
import { ContractContactForm, ContractPaymentType, ContractStatus } from '../enums';

const BASE_URL = '/contracts';

export type ContractSortField =
  | 'studentName'
  | 'contractDate'
  | 'totalAmount'
  | 'remainingAmount'
  | 'nextDueDate'
  | 'overdueDays'
  | 'status';

export const searchContracts: ApiDescription = {
  endpoint: `${BASE_URL}/search`,
  method: RequestsMethod.Get,
};

export type SearchContracts = ApiData<
  {
    page?: string | number;
    limit?: string | number;
    search?: string;
    status?: string;
    strategist?: string;
    churnLevel?: 'low' | 'medium' | 'high';
    hasContract?: 'with' | 'without';
    overdueOnly?: 'checked' | 'unchecked' | 'indeterminate';
    showDeactivated?: 'checked' | 'unchecked' | 'indeterminate';
    sortBy?: ContractSortField | null;
    sortOrder?: 'asc' | 'desc' | null;
  },
  SearchContractsDTO
>;

export const searchContractsLight: ApiDescription = {
  endpoint: `${BASE_URL}/light`,
  method: RequestsMethod.Get,
};

export type SearchContractsLight = ApiData<{ search?: string; limit?: number }, ContractLightDTO[]>;

export const getContract: ApiDescription = {
  endpoint: `${BASE_URL}/:id`,
  method: RequestsMethod.Get,
};

export type GetContract = ApiData<unknown, ContractRowDTO>;

export type ContractPayload = {
  studentId: string;
  contractNumber?: string;
  contractDate: string;
  contractUrl?: string;
  amount: number;
  currency?: string;
  sessionsInContract: number;
  paymentType: ContractPaymentType;
  installmentsCount?: number;
  intervalMonths?: number;
  firstInstallmentDate?: string;
  paymentTermDays?: number;
  installmentAmounts?: number[];
  enrollmentIds?: string[];
};

export const createContract: ApiDescription = {
  endpoint: BASE_URL,
  method: RequestsMethod.Post,
};

export type CreateContract = ApiData<ContractPayload, ContractRowDTO>;

export const updateContract: ApiDescription = {
  endpoint: `${BASE_URL}/:id`,
  method: RequestsMethod.Patch,
};

export type UpdateContract = ApiData<
  Partial<ContractPayload> & {
    caseContext?: string;
    actions?: string;
    retentionSolution?: string;
    sessionsNotes?: string;
    finalStatusOverride?: ContractStatus | null;
    paymentPromise?: { amount: number; date: string; note?: string } | null;
    smartbillClientId?: string | null;
  },
  ContractRowDTO
>;

export const deleteContract: ApiDescription = {
  endpoint: `${BASE_URL}/:id`,
  method: RequestsMethod.Delete,
};

export type DeleteContract = ApiData<unknown, { deleted: boolean }>;

export const addContractContact: ApiDescription = {
  endpoint: `${BASE_URL}/:id/contacts`,
  method: RequestsMethod.Post,
};

export type AddContractContact = ApiData<
  { date: string; form: ContractContactForm; note?: string },
  ContractRowDTO
>;

export const addContractAddendum: ApiDescription = {
  endpoint: `${BASE_URL}/:id/addenda`,
  method: RequestsMethod.Post,
};

export type AddContractAddendum = ApiData<
  { date: string; sessions: number; amount: number; description?: string; url?: string },
  ContractRowDTO
>;

export const deleteContractAddendum: ApiDescription = {
  endpoint: `${BASE_URL}/:id/addenda/:addendumId`,
  method: RequestsMethod.Delete,
};

export type DeleteContractAddendum = ApiData<unknown, ContractRowDTO>;

export const relinkContractEnrollments: ApiDescription = {
  endpoint: `${BASE_URL}/:id/relink-enrollments`,
  method: RequestsMethod.Post,
};

export type RelinkContractEnrollments = ApiData<unknown, ContractRowDTO>;

export const syncContract: ApiDescription = {
  endpoint: `${BASE_URL}/:id/sync`,
  method: RequestsMethod.Post,
};

export type SyncContract = ApiData<unknown, ContractRowDTO>;

export const addContractInvoice: ApiDescription = {
  endpoint: `${BASE_URL}/:id/invoices`,
  method: RequestsMethod.Post,
};

export type AddContractInvoice = ApiData<
  {
    series: string;
    number: string;
    installmentIndex?: number | null;
    issueDate?: string | null;
    dueDate?: string | null;
  },
  ContractRowDTO
>;

export const getUnmappedInvoices: ApiDescription = {
  endpoint: `${BASE_URL}/invoices/unmapped`,
  method: RequestsMethod.Get,
};

export type GetUnmappedInvoices = ApiData<unknown, UnmappedInvoiceDTO[]>;

export const updateClientInvoice: ApiDescription = {
  endpoint: `${BASE_URL}/invoices/:id`,
  method: RequestsMethod.Patch,
};

export type UpdateClientInvoice = ApiData<
  {
    contractId?: string | null;
    installmentIndex?: number | null;
    issueDate?: string | null;
    dueDate?: string | null;
    cancelled?: boolean;
    note?: string;
  },
  UnmappedInvoiceDTO
>;

export const syncClientInvoice: ApiDescription = {
  endpoint: `${BASE_URL}/invoices/:id/sync`,
  method: RequestsMethod.Post,
};

export type SyncClientInvoice = ApiData<unknown, UnmappedInvoiceDTO>;

export const triggerSmartBillSync: ApiDescription = {
  endpoint: `${BASE_URL}/smartbill/sync`,
  method: RequestsMethod.Post,
};

export type TriggerSmartBillSync = ApiData<unknown, SmartBillSyncRunDTO>;

export const getSmartBillSyncStatus: ApiDescription = {
  endpoint: `${BASE_URL}/smartbill/sync-status`,
  method: RequestsMethod.Get,
};

export type GetSmartBillSyncStatus = ApiData<unknown, SmartBillSyncStatusDTO>;

export const triggerPandaDocSync: ApiDescription = {
  endpoint: `${BASE_URL}/pandadoc/sync`,
  method: RequestsMethod.Post,
};

export type TriggerPandaDocSync = ApiData<
  { fullResync?: boolean },
  { message: string; stats: PandaDocSyncStatsDTO }
>;

export type PandaDocSyncStatsDTO = {
  documentsFetched: number;
  documentsMatched: number;
  documentsUnmatched: number;
  contractsCreated: number;
  contractsLinked: number;
  errors: string[];
};

export const getPandaDocDocuments: ApiDescription = {
  endpoint: `${BASE_URL}/pandadoc/documents`,
  method: RequestsMethod.Get,
};

export type GetPandaDocDocuments = ApiData<
  { onlyUnlinked?: 'true' | 'false'; search?: string },
  PandaDocDocumentDTO[]
>;

export const linkPandaDocDocument: ApiDescription = {
  endpoint: `${BASE_URL}/pandadoc/documents/:id/link`,
  method: RequestsMethod.Post,
};

export type LinkPandaDocDocument = ApiData<{ studentId: string }, PandaDocDocumentDTO>;
